import { useMutation } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { retry } from 'src/utils';

import { postPinterestEvent } from '@features/Analytics/api/pinterest';
import type { TpPinterestParameters } from '@features/Analytics/types';
import jsCookies from 'js-cookie';

export const usePinterestEvent = () => {
  const { mutateAsync: postEvent } = useMutation({
    mutationFn: postPinterestEvent,
    mutationKey: ['post-pinterest-event'],
  });

  const conditionCheckFunction = useCallback(
    async () =>
      // @ts-ignore
      window?.pintrk != null,
    [],
  );

  const fetchClientEventWithRetry = useCallback(
    async ({
      eventName,
      options,
    }: Omit<TpPinterestParameters, 'externalId' | 'clickId'>) => {
      const eventId = `${eventName}.${Math.floor(Math.random() * 100_000_000)}`;
      const actionFunction = async () => {
        // @ts-ignore
        window?.pintrk?.('track', eventName, { event_id: eventId, ...options });
      };
      const clickId = jsCookies.get('_epik');
      const externalId =
        jsCookies.get('_ga') ?? `${Math.floor(Math.random() * 100_000_000)}`;

      await retry({
        conditionCheckFunction,
        actionFunction,
        retryDelay: 300,
      });

      await postEvent({
        eventName,
        eventId,
        options,
        externalId,
        clickId,
      });
    },
    [conditionCheckFunction, postEvent],
  );

  return useMemo(
    () => ({
      clientEvent: ({
        eventName,
        options,
      }: Omit<TpPinterestParameters, 'externalId' | 'clickId'>) => {
        fetchClientEventWithRetry({
          eventName,
          options,
        });
      },
    }),
    [fetchClientEventWithRetry],
  );
};
